import type Keycloak from 'keycloak-js';
import { AuthService, SIGNED_OUT } from './auth.service';
import { HistorySafeLocation, redirectToLogin } from './redirect-to-login';

export type BootKeycloak = Pick<
  Keycloak,
  'init' | 'createLoginUrl' | 'updateToken' | 'onTokenExpired'
>;

/** Signs the visitor in through `idpHint` and loads their identity. Never rejects: a failure
 *  is left on `auth.accessFailure` for the app to render. */
export async function bootAuth(
  keycloak: BootKeycloak,
  auth: AuthService,
  location: HistorySafeLocation & { readonly href: string },
  idpHint: string,
): Promise<void> {
  try {
    // No onLoad: 'login-required' forwards only prompt/locale to login() and drops idpHint.
    const authenticated = await keycloak.init({
      pkceMethod: 'S256',
      checkLoginIframe: false,
      // A stale #state/#code left by a failed callback must not ride into the next redirect_uri.
      redirectUri: location.href.split('#')[0],
    });
    if (!authenticated) {
      if (new URL(location.href).searchParams.has(SIGNED_OUT)) {
        auth.markSignedOut();
      } else {
        await redirectToLogin(keycloak, idpHint, location);
      }
      return;
    }
  } catch (err: unknown) {
    console.error('Keycloak init failed', err);
    auth.markUnavailable();
    return;
  }
  keycloak.onTokenExpired = () => void keycloak.updateToken(30);
  try {
    await auth.ensureIdentity();
  } catch (err: unknown) {
    console.error('Identity load failed', err);
  }
}
